import React, { useEffect, useState, useRef } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { signOut, signInWithCustomToken } from "firebase/auth";
import { httpsCallable } from "firebase/functions";

import { AdvancedSettings, AppShellResponsive, Assets, Dashboard, Login, NotificationConfiguration, Users, FlexNotificationConfiguration, AiAssistantScreen, Register, AnomaliesScreen } from "./screens";
import { CaptivaFuelConfigs } from "./screens/captiva-fuel-configs";

import { auth, functions } from "./firebase";
import { Reports } from "./screens/reports";

export function clearOnboardingSession(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith("captiva_onboarding_")) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => sessionStorage.removeItem(key));
  } catch (e) {}
}
import { MantineProvider, Alert } from "@mantine/core";
import { ResetPassword } from "./screens/reset-password";
import { AdvancedReports } from "./screens/advanced-reports";
import { ConfirmEmail } from "./screens/confirm-email";
import Trips from "./screens/trips";
import AggregatedTripsMap from "./screens/aggregated-trips-map";
import FormTemplates from "./screens/form-templates";
import FormSelectOptions from "./screens/form-select-options";
import FlexReports from "./screens/flex-reports";

import FormTemplateSubmissions from "./screens/form-template-submissions";
import CompanyOverview from "./screens/company-overview";
import E2EClickableEntities from "./screens/e2e-clickable-entities";
import { LoginLinkLanding } from "./screens/login-link";
import { ErrorBoundary } from "./components";
import { useFeatureFlags } from "./hooks/useFeatureFlags";

export function AppRoot() {
  const [authUser, authLoading, error] = useAuthState(auth);
  const e2eHarnessEnabled = process.env.REACT_APP_ENABLE_E2E_HARNESS === "true";
  const [authValue, setAuthValue] = useState<any>(undefined);
  const [companyId, setCompanyId] = useState<string>("");
  const [isRedeeming, setIsRedeeming] = useState(false);
  const processedRef = useRef<string | null>(null);
  const { anomalyDetectionEnabled } = useFeatureFlags(companyId);

  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const hasPendingNonce =
    location.pathname === "/dashboard/overview" && !!searchParams.get("nonce");

  const isActionLinkRoot =
    location.pathname === "/" &&
    searchParams.has("oobCode") &&
    searchParams.has("mode");
  const isPublicRoute =
    location.pathname === "/" ||
    location.pathname === "/login" ||
    location.pathname === "/reset" ||
    location.pathname === "/verify" ||
    location.pathname === "/auth/action" ||
    location.pathname === "/__/auth/action" ||
    location.pathname === "/register" ||
    location.pathname.startsWith("/l/") ||
    (e2eHarnessEnabled && location.pathname.startsWith("/__e2e__/"));

  const fetchClaim = async () => {
    if (auth.currentUser && !auth.currentUser.isAnonymous) {
      auth.currentUser
        .getIdTokenResult(true)
        .then((idTokenResult) => {
          setAuthValue(idTokenResult);
          setCompanyId((idTokenResult.claims.companyId as string) || "");
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      setAuthValue(undefined);
      setCompanyId("");
    }
  };

  useEffect(() => {
    if (authLoading) return;

    const params = new URLSearchParams(location.search);
    const fresh = params.get("fresh");
    const uidHint = params.get("uid");
    const nonce = params.get("nonce");

    if (fresh === "1") {
      if (auth.currentUser || authUser) {
        signOut(auth).catch((err) => console.error("SignOut on fresh error:", err));
      }
      clearOnboardingSession();
      params.delete("fresh");
      const cleanSearch = params.toString() ? `?${params.toString()}` : "";
      if (typeof window !== "undefined" && window.history?.replaceState) {
        window.history.replaceState(null, "", location.pathname + cleanSearch);
      }
      navigate({ pathname: location.pathname, search: cleanSearch }, { replace: true });
      return;
    }

    if (location.pathname === "/dashboard/overview" && (uidHint || nonce)) {
      const tokenKey = `${location.pathname}_${uidHint || ""}_${nonce || ""}`;
      if (processedRef.current === tokenKey) return;
      processedRef.current = tokenKey;

      params.delete("nonce");
      params.delete("uid");
      const cleanSearch = params.toString() ? `?${params.toString()}` : "";
      if (typeof window !== "undefined" && window.history?.replaceState) {
        window.history.replaceState(null, "", location.pathname + cleanSearch);
      }
      navigate({ pathname: location.pathname, search: cleanSearch }, { replace: true });

      const currentUid = auth.currentUser?.uid || authUser?.uid;

      if (uidHint && currentUid === uidHint) {
        return;
      }

      (async () => {
        try {
          if (nonce) {
            setIsRedeeming(true);
          }
          if (currentUid && uidHint && currentUid !== uidHint) {
            await signOut(auth);
          }

          if (nonce) {
            const callable = httpsCallable<{ nonce: string }, { customToken: string }>(
              functions,
              "redeemLoginNonce"
            );
            const res = await callable({ nonce });
            const customToken = res?.data?.customToken;
            if (!customToken) {
              throw new Error("Missing customToken from redeemLoginNonce");
            }
            await signInWithCustomToken(auth, customToken);
          }
        } catch (err) {
          console.error("Dashboard nonce redemption failed:", err);
          try {
            await signOut(auth);
          } catch {}
          navigate("/");
        } finally {
          setIsRedeeming(false);
        }
      })();
    }
  }, [location.pathname, location.search, authLoading, authUser, navigate]);

  useEffect(() => {
    if (authLoading || isRedeeming || hasPendingNonce) return;
    const isAuthenticated = !!authUser && !authUser.isAnonymous;
    if (!isAuthenticated && !isPublicRoute) {
      setAuthValue(undefined);
      setCompanyId("");
      return navigate("/");
    }
    if (error) console.log(error);

    if (isAuthenticated) {
      fetchClaim();
    } else {
      setAuthValue(undefined);
      setCompanyId("");
    }
  }, [authUser, authLoading, error, navigate, isPublicRoute, isRedeeming, hasPendingNonce]);

  if ((authLoading || isRedeeming || hasPendingNonce) && !isPublicRoute) {
    return (
      <MantineProvider withNormalizeCSS withGlobalStyles>
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
          <div>Loading...</div>
        </div>
      </MantineProvider>
    );
  }

  return (
    <MantineProvider
      withNormalizeCSS
      withGlobalStyles
      theme={{
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        fontFamilyMonospace: "'Roboto Mono', SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
        headings: {
          fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
        },
        colors: {
          "cap-navy": [
            "#434872",
            "#373E6D",
            "#2C3369",
            "#212967",
            "#162065",
            "#0B1665",
            "#000C66",
            "#091253",
            "#0F1544",
            "#121738",
            "#14172F",
            "#141728",
            "#141622",
          ],
          "cap-purple": [
            "#F3F1F8",
            "#D8D1EC",
            "#BCAFE5",
            "#A18AE5",
            "#8361ED",
            "#6331FF",
            "#582AE7",
            "#532CCA",
            "#5236A8",
            "#4F3B8D",
            "#4B3C77",
            "#463C66",
          ],
          "cap-blue": [
            "#E0E4F4",
            "#BBC5EF",
            "#92A5F1",
            "#6482FC",
            "#5471E9",
            "#4965D4",
            "#425BBF",
            "#4658A1",
            "#475489",
            "#464F75",
          ],
          "cap-orange": [
            "#EAE0DA",
            "#DEC9BD",
            "#D7B39F",
            "#D59E7E",
            "#DB895A",
            "#E87330",
            "#FF5D00",
            "#CF5A17",
            "#A95525",
            "#8D502D",
            "#764A31",
            "#644533",
            "#563F32",
          ],
          "cap-yellow": [
            "#FDFDFB",
            "#F2EDD8",
            "#EEE2B2",
            "#F2DD89",
            "#FFDE59",
            "#EBCB4B",
            "#D5B841",
            "#BEA43D",
            "#9F8D43",
            "#877A44",
            "#736A43",
          ],
          "cap-green": [
            "#005a00",
            "#008000",
            "#bfffbf",
            "#80ff80",
            "#005a00",
            "#005a00",
            "#005a00",
            "#005a00",
            "#005a00",
            "#005a00",
            "#005a00",
          ],
          gray: [
            "#f8f9fa",
            "#f1f3f5",
            "#e9ecef",
            "#dee2e6",
            "#ced4da",
            "#adb5bd",
            "#595959",
            "#495057",
            "#343a40",
            "#212529",
          ],
        } as any,
      }}
    >
      <ErrorBoundary companyId={companyId} userEmail={authUser?.email || undefined}>
        <AppShellResponsive loading={authLoading} user={isPublicRoute || !authUser || (authUser as any)?.isAnonymous ? null : authUser as any} companyId={companyId} auth={authValue}>
          <Routes>
            {e2eHarnessEnabled && (
              <Route
                path="/__e2e__/clickable-entities"
                element={<E2EClickableEntities />}
              />
            )}
            <Route path="/" element={isActionLinkRoot ? <ConfirmEmail /> : <Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/l/:nonce" element={<LoginLinkLanding />} />
            <Route
              path="/reset"
              element={<ResetPassword />}
            />
            <Route
              path="/verify"
              element={<ConfirmEmail />}
            />
            <Route
              path="/auth/action"
              element={<ConfirmEmail />}
            />
            <Route
              path="/__/auth/action"
              element={<ConfirmEmail />}
            />
            <Route
              path="/dashboard"
              element={
                <Navigate
                  to={{ pathname: "/dashboard/overview", search: location.search }}
                  replace
                />
              }
            />
            <Route
              path="/dashboard/overview"
              element={<CompanyOverview companyId={companyId} />}
            />
            <Route
              path="/dashboard/ai-assistant"
              element={<AiAssistantScreen companyId={companyId} />}
            />
            {anomalyDetectionEnabled && (
              <Route
                path="/dashboard/anomalies"
                element={<AnomaliesScreen companyId={companyId} />}
              />
            )}

            <Route
              path="/dashboard/settings"
              element={<Dashboard auth={authValue} />}
            />
            <Route
              path="/dashboard/trips"
              element={<Trips auth={authValue} companyId={companyId} />}
            />
            <Route
              path="/dashboard/aggregated-trips-map"
              element={<AggregatedTripsMap companyId={companyId} />}
            />

            <Route
              path="/dashboard/users"
              element={<Users auth={authValue} companyId={companyId} />}
            />
            <Route
              path="/dashboard/advanced-settings"
              element={<AdvancedSettings companyId={companyId} />}
            />
            <Route
              path="/dashboard/reports"
              element={<Reports companyId={companyId} />}
            />
            <Route
              path="/dashboard/advanced-reports"
              element={<AdvancedReports companyId={companyId} />}
            />
            <Route
              path="/dashboard/assets"
              element={<Assets auth={authValue} companyId={companyId} />}
            />
            <Route
              path="/dashboard/notifications"
              element={<NotificationConfiguration companyId={companyId} />}
            />
            <Route
              path="/dashboard/captiva-fuel-configs"
              element={<CaptivaFuelConfigs companyId={companyId} />}
            />
            <Route
              path="/dashboard/form-templates"
              element={<FormTemplates companyId={companyId} auth={authValue} />}
            />
            <Route
              path="/dashboard/form-select-options"
              element={<FormSelectOptions companyId={companyId} />}
            />
            <Route
              path="/dashboard/form-template-submissions"
              element={<FormTemplateSubmissions companyId={companyId} />}
            />
            <Route
              path="/dashboard/flex-reports"
              element={<FlexReports companyId={companyId} />}
            />
            <Route
              path="/dashboard/flex-notifications"
              element={<FlexNotificationConfiguration companyId={companyId} />}
            />
            <Route
              path="/dashboard/:templateName"
              element={<Dashboard auth={authValue} />}
            />
          </Routes>
        </AppShellResponsive>
      </ErrorBoundary>
    </MantineProvider>
  );
}
